import { useMemo } from "react";

import {
  CoilType as CoilTypeType,
  DuctType,
  type Coil as CoilType,
  type Damper as DamperType,
} from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";

import { m } from "~/paraglide/messages";

import { hasDuctReading, type DuctSensors } from "../helpers";
import { Position } from "../types";
import { Antifreeze } from "./anti-freeze";
import { Coil } from "./coil";
import { CoilDialog } from "./coil-dialog";
import { Damper } from "./damper";
import { DamperDialog } from "./damper-dialog";
import { Filter } from "./filter";
import { Inlet } from "./inlet";
import * as RecirculationDamper from "./recirculation-damper";
import { Sensors } from "./sensors";

interface OutsideProps {
  buildingId: string;
  airHandlerUnitId: string;
  position?: Position;
  temperature?: number;
  temperatureDefect?: boolean;
  damper?: DamperType;
  recirculationDamper?: boolean;
  airFilterPressureSwitchDefect?: boolean;
  preFilterPressureSwitchDefect?: boolean;
  sensors?: DuctSensors;
  coils?: CoilType[];
  antiFreezeDefect?: boolean;
}

const Outside: React.FC<OutsideProps> = ({
  buildingId,
  airHandlerUnitId,
  position = Position.TOP,
  temperature,
  temperatureDefect,
  damper,
  recirculationDamper,
  airFilterPressureSwitchDefect,
  preFilterPressureSwitchDefect,
  sensors,
  coils,
  antiFreezeDefect,
}) => {
  const hydraulicHeatingCoil = useMemo(() => coils?.find(c => c.coilType === CoilTypeType.HYDRAULIC_HEATING), [coils]);
  const hydraulicCoolingCoil = useMemo(() => coils?.find(c => c.coilType === CoilTypeType.HYDRAULIC_COOLING), [coils]);

  const coilsDisplayed = !!hydraulicHeatingCoil || !!hydraulicCoolingCoil;

  return (
    <div
      className={cn(
        "gap-2.5 pr-2.5 relative flex flex-row items-center [grid-area:outside] *:shrink-0",
        "after:left-16.25 after:h-23.5 after:absolute after:-z-2 after:w-[calc(100%-65px)] after:border-y after:border-primary-dark-gray after:bg-neutral-100 after:content-['']",
        position === Position.TOP ? "*:self-end after:-bottom-px" : "*:self-start after:-top-px",
      )}
    >
      <Inlet
        buildingId={buildingId}
        airHandlerUnitId={airHandlerUnitId}
        type={DuctType.OUTSIDE}
        position={position}
        temperature={temperature}
        temperatureDefect={temperatureDefect}
      />
      {damper && (
        <DamperDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} damper={damper}>
          <Damper {...damper} position={position} />
        </DamperDialog>
      )}
      {recirculationDamper && <RecirculationDamper.Spacer />}
      {preFilterPressureSwitchDefect !== undefined && (
        <Filter
          position={position}
          defect={preFilterPressureSwitchDefect}
          label={m.air_handler_unit_filter_outside_pre()}
        />
      )}
      {airFilterPressureSwitchDefect !== undefined && (
        <Filter
          position={position}
          defect={airFilterPressureSwitchDefect}
          label={m.air_handler_unit_filter_outside()}
        />
      )}
      {sensors && hasDuctReading(sensors) && (
        <Sensors buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} position={position} {...sensors} />
      )}
      {coilsDisplayed && (
        <>
          {hydraulicHeatingCoil && (
            <CoilDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} coil={hydraulicHeatingCoil}>
              <Coil {...hydraulicHeatingCoil} position={position} />
            </CoilDialog>
          )}
          {antiFreezeDefect !== undefined ? (
            <Antifreeze position={position} defect={antiFreezeDefect} />
          ) : (
            <div className="w-4" />
          )}
          {hydraulicCoolingCoil && (
            <CoilDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} coil={hydraulicCoolingCoil}>
              <Coil {...hydraulicCoolingCoil} position={position} />
            </CoilDialog>
          )}
        </>
      )}
      {!coilsDisplayed && antiFreezeDefect !== undefined && (
        <Antifreeze position={position} defect={antiFreezeDefect} />
      )}
    </div>
  );
};

Outside.displayName = "Outside";

export { Outside };
export type { OutsideProps };
